import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { type OrganisationWalletSummaryBreakdown } from 'src/core/modules/organisation-reporting/types';
import { formatMonetaryValue } from 'src/core/utils/monetaryValue';

import {
  BreakdownGauge,
  type BreakdownGaugeBar,
  BreakdownGaugeLegend,
} from '../../../common/BreakdownGauge';

type Props = {
  currency: string;
  breakdown: OrganisationWalletSummaryBreakdown;
};

export const GroupBreakdownBlock = ({ currency, breakdown }: Props) => {
  const { t } = useTranslation('global');

  const formatAsMonetaryValue = (amount: number): string =>
    formatMonetaryValue({
      amount,
      currency,
      precision: 2,
    });

  const breakdownBars: BreakdownGaugeBar[] = [
    {
      key: 'scheduled-transfers',
      name: t(
        'organisation.reporting.page.entities.entity.breakdown.scheduledTransfers.label',
      ),
      value: {
        raw: breakdown.scheduledTransfers,
        formatted: formatAsMonetaryValue(breakdown.scheduledTransfers),
      },
      tooltip: t(
        'organisation.reporting.page.group.breakdown.scheduledTransfers.tooltip',
      ),
    },
    {
      key: 'card-allocations',
      name: t(
        'organisation.reporting.page.entities.entity.breakdown.cardsLimits.label',
      ),
      value: {
        raw: breakdown.cardsLimits,
        formatted: formatAsMonetaryValue(breakdown.cardsLimits),
      },
      tooltip: t(
        'organisation.reporting.page.group.breakdown.cardsLimits.tooltip',
      ),
    },
    {
      key: 'shortfall',
      name: t('organisation.reporting.page.group.breakdown.shortfall.label'),
      value: {
        raw: breakdown.shortfall,
        formatted: formatAsMonetaryValue(breakdown.shortfall),
      },
      subText: t('organisation.reporting.page.entities.entity.count', {
        count: breakdown.shortfallCount,
      }),
      tooltip: t(
        'organisation.reporting.page.group.breakdown.shortfall.tooltip',
      ),
    },
    {
      key: 'available',
      name: t('organisation.reporting.page.group.breakdown.available.label'),
      value: {
        raw: breakdown.available,
        formatted: formatAsMonetaryValue(breakdown.available),
      },

      subText: t('organisation.reporting.page.entities.entity.count', {
        count: breakdown.availableCount,
      }),
      tooltip: t(
        'organisation.reporting.page.group.breakdown.available.tooltip',
      ),
    },
  ];

  return (
    <div>
      <BreakdownGauge
        bars={breakdownBars}
        trend={breakdown.trend}
        isInactive={false}
      >
        {(preparedBars) => <BreakdownGaugeLegend preparedBars={preparedBars} />}
      </BreakdownGauge>
    </div>
  );
};
